import { Fragment } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { ProductScope } from '../data/Constants';
import { VariableSummary } from '.';
import { ExpandMore } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { AppState, AppStore } from '../store';
import {
  getFormattedPrice,
  getAssignedValue,
  getSubmodelIdFromSectionId,
  getSubModelsFromSectionId,
  getVariableIdWithoutPrefixes,
  getViewIdSuffix,
  isScope,
  isSubModel,
  isSubModelAssigned,
  isSubmodelCountVariable,
  getNameWithCode,
  getSymbol,
  removeViewSuffix,
} from '../services';
import '../../public/svg/level0.svg';
import '../../public/svg/level1.svg';
import '../../public/svg/level2.svg';
import '../../public/svg/level3.svg';
import '../../public/svg/level4.svg';
import '../../public/svg/level5.svg';
import { t } from 'i18next';
import { isFeatureActive, isFeatureVisible } from '../services/ClaimsHelper';
import { isValidCurrency } from '../services/Price';
import type {
  ISectionSummaryCreateSection,
  ISectionSummaryRenderScope,
  ISectionSummaryRenderModel,
  ISummaryAccordionHeader,
  ISummarySectionProps,
  IBasePriceAccordion,
  ISection,
  IConfigurationVariable,
  ISectionPrice,
} from '../../types';

/**
 * Returns true if section id represents a model.
 * @param {string} id - The id to be checked
 * @returns {boolean} Whether the id represents a model or not
 */
function isModelSection(id: string) {
  return id.endsWith(getViewIdSuffix());
}

/**
 * Renders all non-model sections and assigned variables in the given section.
 * @param {ISectionSummaryCreateSection} param0 - The props required by the component
 * @returns {JSX.Element} Component displaying non-model sections with selected variables' summary
 */
function createSectionComponent({
  withTitle,
  section,
  depth,
  modelPath,
  isSimpleConfigurationSummary,
}: ISectionSummaryCreateSection) {
  //Filter sections to not show Submodel sections or Scope section or no sections and variables are present in a particular section
  const sections = section.sections?.filter(
    (s) =>
      !(
        isSubModel(s as ISection) ||
        isScope(s as ISection) ||
        (s.sections.length === 0 && s.variables.length === 0)
      )
  );
  const variables = section.variables?.filter(
    (v) => !(isSubmodelCountVariable(v) || v.isMarkedOptional)
  );
  let title = null;
  //Hiding the Section Name
  if (withTitle && !isSimpleConfigurationSummary) {
    title = <p className="contentHead">{section.name}</p>;
  }
  return (
    <Box className="sectionSummary content">
      {title}
      <Box className="contentBody">
        {/*Passing isScopeVariable to the VariableSummary component*/}
        {variables?.map((v) => (
          <VariableSummary
            key={`variable-${v.id}`}
            variable={v}
            isScopeVariable={false}
            isSimpleConfigurationSummary={isSimpleConfigurationSummary}
          />
        ))}
        {sections.map((s) => (
          <SectionSummary key={`section-${s.id}`} section={s} depth={depth} modelPath={modelPath} />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Renders the Optional items summary for a section.
 * @param {ISectionSummaryCreateSection} param0 - The props required by the component
 * @returns {JSX.Element} Component displaying Optional items summary
 */
function createOptionalComponent({
  withTitle,
  section,
  depth,
  modelPath,
  isSimpleConfigurationSummary,
}: ISectionSummaryCreateSection) {
  const sections = section.sections?.filter(
    (s) => !(isSubModel(s as ISection) || isScope(s as ISection))
  );
  const variables = section.variables?.filter((v) => !isSubmodelCountVariable(v));
  let title = null;
  //Hiding the Section Name
  if (withTitle && !isSimpleConfigurationSummary) {
    title = <p className="contentHead">{section.name}</p>;
  }
  return (
    <Box className="sectionSummary content">
      {title}
      <Box className="contentBody">
        {/*Passing isScopeVariable to the VariableSummary component*/}
        {variables?.map((v, key) => (
          <VariableSummary
            key={`variable-${v.id + key}`}
            variable={v}
            isScopeVariable={false}
            showOptional={true}
            isSimpleConfigurationSummary={isSimpleConfigurationSummary}
          />
        ))}
        {sections.map((s) => (
          <Fragment key={`section-${section.id}-${s.id}`}>
            {createOptionalComponent({
              withTitle: true,
              section: s,
              depth,
              modelPath,
              isSimpleConfigurationSummary,
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Returns the header component for the accordion depending on the model type.
 * @param {ISummaryAccordionHeader} param0 - The props required by the component
 * @returns {JSX.Element} Component representing the header for the model accordion
 */
function summaryAccordionHeader({ section, depth, getCountVariable }: ISummaryAccordionHeader) {
  return (
    <Box className="summaryAccordionHeader">
      <img src={`../../public/svg/level${Math.min(depth, 5)}.svg`} alt="accordian img" />
      &nbsp;
      <span className="summary-accordianTitle">
        <h6 className={'summary-accordionHead'}>
          <span className={`summary-accordionHeadTitle ${section.isComplete ? '' : 'required'}`}>
            {isSubModel(section)
              ? `${section.name} (x${getAssignedValue(getCountVariable?.(section))?.value})`
              : `${section.name}`}
          </span>
          {!section.isComplete && (
            <span className="asterisk" aria-hidden="true">
              *
            </span>
          )}
        </h6>
        &nbsp;
      </span>
    </Box>
  );
}
// Shows Base Price Content of Models on Summary Panel if available
function basePriceHeader(
  { section, getCountVariable, showCode }: IBasePriceAccordion,
  currency: string,
  countryCode: string
) {
  return (
    <Table size="small" aria-label="Base Model Summary Table">
      <TableBody>
        <TableRow>
          <TableCell component="th" className={'baseModelName '}>
            {
              <span className="baseHead">
                <b>
                  {isSubModel(section)
                    ? `${getNameWithCode({ id: section.productId, name: section.name }, showCode, true)} (x${getAssignedValue(getCountVariable?.(section))?.value})`
                    : getNameWithCode(
                        { id: section.productId, name: section.name },
                        showCode,
                        true
                      )}
                </b>
              </span>
            }
          </TableCell>
          <TableCell component="th" className="baseModelPriceCell">
            <span className="content" color="text.secondary">
              {currency && getSymbol(countryCode, currency)}
              {section.basePrice && section.basePrice >= 0 && getFormattedPrice(section.basePrice)}
            </span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/**
 * Renders the summary header for an accordion, including price if available.
 * @param {ISectionSummaryRenderScope} props - The props required by the component
 * @returns {JSX.Element} The rendered accordion summary
 */
const RenderAccordionSummary = ({
  setAccordionValue,
  section,
  depth,
  getCountVariable,
  currency,
  countryCode,
  featureFlags,
}: ISectionSummaryRenderScope) => {
  return (
    <AccordionSummary
      onClick={() => setAccordionValue?.(section.id)}
      className="acSummary"
      expandIcon={<ExpandMore />}
    >
      {summaryAccordionHeader({ section, depth, getCountVariable })}
      {isFeatureVisible(featureFlags.ListPrice) && (
        <Box className="modelPrice">
          <span>
            <span className={isValidCurrency(currency) ? '' : 'invalidCurrency'}>
              {currency && getSymbol(countryCode, currency)}
            </span>
            {currency &&
              getFormattedPrice(
                section.basePrice ? section.basePrice + section.price : section.price
              )}
          </span>
        </Box>
      )}
    </AccordionSummary>
  );
};

/**
 * Renders the base price header for a section if available.
 * @param {ISectionPrice} section - The section object
 * @param {Function} getCountVariable - Function to get count variable
 * @param {boolean} showCode - Whether to show code
 * @param {string} currency - Currency code
 * @param {string} countryCode - Country code
 * @returns {JSX.Element | null} The base price header or null
 */
const sectionBasePrice = (
  section: ISectionPrice,
  getCountVariable: ((modelSection: ISection) => IConfigurationVariable | null) | undefined,
  showCode: boolean,
  currency: string,
  countryCode: string
) => {
  if (section.basePrice && section.basePrice >= 0) {
    return basePriceHeader({ section, getCountVariable, showCode }, currency, countryCode);
  } else {
    return null;
  }
};

/**
 * Returns a divider element if not in simple configuration summary mode.
 * @returns {JSX.Element | null} The divider or null
 */
const getDivider = () => {
  const featureFlags = AppStore.getState().claimsData.featureFlags;
  const isSimpleConfigurationSummary = isFeatureActive(featureFlags.SimpleConfigurationSummary);
  return !isSimpleConfigurationSummary ? <Divider className="divider" /> : null;
};

/**
 * Returns an Accordion that represents the model's summary.
 * @param {ISectionSummaryRenderScope} param0 - The props required by the component
 * @returns {JSX.Element | null} The Accordion that represents the model's summary
 */
function renderModel({
  section,
  modelPath,
  depth,
  accordionValue,
  setAccordionValue,
  currency,
  getCountVariable,
  showCode,
  countryCode,
  featureFlags,
  isSimpleConfigurationSummary,
}: ISectionSummaryRenderScope) {
  const currentModel = getSubmodelIdFromSectionId(section.id);
  return modelPath.length === 1 ||
    depth >= modelPath.length ||
    (depth === modelPath.length - 1 && modelPath[depth] === currentModel) ? (
    <Accordion className="sectionSummary root" expanded={accordionValue === section.id}>
      <RenderAccordionSummary
        setAccordionValue={setAccordionValue}
        section={section}
        depth={depth}
        getCountVariable={getCountVariable}
        currency={currency}
        countryCode={countryCode}
        featureFlags={featureFlags}
        modelPath={modelPath}
        showCode={showCode}
        isSimpleConfigurationSummary={isSimpleConfigurationSummary}
      />
      {isFeatureVisible(featureFlags.ListPrice) &&
        sectionBasePrice(section, getCountVariable, showCode, currency, countryCode)}
      <AccordionDetails>
        {createSectionComponent({
          withTitle: false,
          section,
          depth,
          modelPath,
          isSimpleConfigurationSummary,
        })}
        {(isFeatureVisible(featureFlags.Optional) && section.optional && (
          <>
            {getDivider()}
            <p className="contentHead">
              <u>{`${t('labels.optionalItems')}`}</u>
            </p>
            {createOptionalComponent({
              withTitle: false,
              section: section.optional,
              depth,
              modelPath,
              isSimpleConfigurationSummary,
            })}
          </>
        )) ||
          null}
      </AccordionDetails>
    </Accordion>
  ) : null;
}

/**
 * Returns an Accordion displaying the scope for the configuration.
 * @param {ISectionSummaryRenderModel} param0 - The props required by the component
 * @returns {JSX.Element} Accordion displaying the scope for the configuration
 */
function renderScope({
  section,
  accordionValue,
  setAccordionValue,
  isSimpleConfigurationSummary,
}: ISectionSummaryRenderModel) {
  return (
    <Accordion
      className="sectionSummary root"
      expanded={accordionValue === section.id}
      onClick={() => setAccordionValue?.(section.id)}
    >
      <AccordionSummary className="acSummary" expandIcon={<ExpandMore />}>
        <h6 className="accordionHead">{t(`labels.${section.name}`)}</h6>
      </AccordionSummary>
      <AccordionDetails>
        <Box className="sectionSummary content">
          <Box className="contentBody">
            {section.variables?.map((v) => (
              <VariableSummary
                key={`variable-${v.id}`}
                variable={v}
                isScopeVariable={true}
                isSimpleConfigurationSummary={isSimpleConfigurationSummary}
              />
            ))}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Handles subsections and variables in configuration summary.
 * Renders first variables of the section, then subsections.
 * @param {ISummarySectionProps} props - The properties for the section summary component
 * @returns {JSX.Element | null} The section summary component
 */
export const SectionSummary = ({
  section,
  depth,
  accordionValue,
  getCountVariable,
  setAccordionValue,
  displayScope,
  modelPath,
}: ISummarySectionProps) => {
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const configuration = useSelector((state: AppState) => state.configuration);
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);

  //If this feature flag is active, both Section Name and Feature Family information will be hidden
  const featureFlags = AppStore.getState().claimsData.featureFlags;
  const isSimpleConfigurationSummary = isFeatureActive(featureFlags.SimpleConfigurationSummary);

  if (!section || !modelPath || !configuration || !applicationSettings) {
    return null;
  }
  const submodels = getSubModelsFromSectionId(section.id);
  const isSubmodel = isSubModel(section);
  const currentModel = getSubmodelIdFromSectionId(section.id);
  const models = section.sections?.filter((s) => isSubModel(s as ISection));
  if (isSubmodel && !isSubModelAssigned(configuration, submodels)) {
    return null;
  }
  //Add property product id to submodels
  if (isSubmodel) {
    section.productId = removeViewSuffix(section.id);
  }
  if (displayScope) {
    section.variables = section.variables.filter(
      (variable) => ProductScope.EventId !== getVariableIdWithoutPrefixes(variable)
    );
    return renderScope({
      section,
      accordionValue,
      setAccordionValue,
      isSimpleConfigurationSummary,
    });
  } else if (isModelSection(section.id)) {
    return (
      <>
        {renderModel({
          section,
          modelPath,
          depth,
          accordionValue,
          setAccordionValue,
          currency: applicationSettings.currency.currencyCode,
          getCountVariable,
          showCode,
          countryCode: applicationSettings.currency.countryCode,
          featureFlags,
          isSimpleConfigurationSummary,
        })}
        {modelPath.length === 1 || (depth < modelPath.length && modelPath[depth] === currentModel)
          ? models?.map((s) => (
              <SectionSummary
                key={`section-${s.id}`}
                section={s}
                depth={depth + 1}
                accordionValue={accordionValue}
                setAccordionValue={setAccordionValue}
                modelPath={modelPath}
                getCountVariable={getCountVariable}
              />
            ))
          : null}
      </>
    );
  }
  return createSectionComponent({
    withTitle: true,
    section,
    depth,
    modelPath,
    isSimpleConfigurationSummary,
  });
};
